import React, { useEffect, useRef, useState } from "react";
import { CsaQuestionPhotos } from "./CsaPhotos";
import { PreviousLine } from "./CsaCarry";
import * as api from "./csaApi";
/*
 * isGap is shared rather than redefined here, so the rule that decides which
 * answers owe an explanation cannot disagree with the rule that decides which
 * ones may be tidied up.
 */
import { shouldOfferAssist, isGap } from "./assistRules";

/**
 * One question on the capture screen.
 *
 * Marking and writing are separated (design §7e): tapping an answer saves at
 * once, and the note box is present but never a gate. Submit is what enforces
 * a note on every gap, so an auditor standing in a plant can mark 163 items
 * without typing once.
 *
 * The note box is a plain <textarea> on purpose. The tablet keyboard's own
 * dictation runs on-device and works with no signal; a custom voice control
 * would use the Web Speech API, which needs a network the plant floor does not
 * have, and would break the thing that already works.
 */

const OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "partial", label: "Partial" },
  { value: "no", label: "No" },
  { value: "na", label: "N/A" },
];

/*
 * What a good comment looks like, by how the question is evidenced.
 *
 * One example used to serve all 163 questions — a machinery-guarding note
 * offered under "Is there a documented HSE policy?", which tells an auditor
 * nothing about what to write and quietly suggests the wrong kind of answer.
 *
 * The framework records 21 combinations of evidence type, but only six
 * primitives; the first one named is the one that shapes the comment, so these
 * six cover every question.
 *
 * Each example is specific and carries a number or a place, because that is
 * exactly what the drafting substance floor requires before a finding is sent
 * to a model. A placeholder that models a thin comment would teach auditors to
 * write comments their own report cannot use.
 */
const EXAMPLES = {
  "document review":
    "e.g. Policy last approved March 2023; not reissued after the site reorganisation.",
  "record verification":
    "e.g. Register last signed 14 months ago; three entries have no closure date.",
  "physical verification":
    "e.g. Two of the six extinguishers in Bay 4 are past their test date.",
  observation:
    "e.g. Interlock bypassed on press line 3; guard removed and stored beside the machine.",
  interview:
    "e.g. Two of the four supervisors could not say who the responsible person is.",
  "functional test":
    "e.g. Alarm sounded in Bay 2 but not in the paint booth.",
};

const FALLBACK =
  "What you saw, where, and how many — e.g. Register last signed 14 months ago; three entries incomplete.";

/** The first evidence type named is the one that shapes the answer. */
function exampleFor(evidence) {
  const first = String(evidence || "").split(";")[0].trim().toLowerCase();
  return EXAMPLES[first] || FALLBACK;
}

export default function CsaQuestionCard({
  question,
  auditId,
  answer,
  photos,
  disabled,
  scopedOutBy,
  previous,
  assistEnabled,
  onAnswer,
  onComment,
  onConfirm,
  onPhotosChanged,
}) {
  const [showRef, setShowRef] = useState(false);
  const [draft, setDraft] = useState((answer && answer.comment) || "");
  const timer = useRef(null);
  const dirty = useRef(false);

  /*
   * Tier 3 (design §8). One of: null, "working", or a result — either a
   * suggestion to look at or advice about why there is not one yet.
   */
  const [assist, setAssist] = useState(null);

  const value = (answer && answer.answer) || null;

  /*
   * Adopt the server's text only while the auditor is not mid-sentence.
   * Without the dirty check, a roll-up refresh landing between keystrokes
   * would snap the box back to the last saved value and eat the typing.
   */
  useEffect(() => {
    if (dirty.current) return;
    setDraft((answer && answer.comment) || "");
  }, [answer]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flush = (text) => {
    clearTimeout(timer.current);
    if (!dirty.current) return;
    dirty.current = false;
    onComment(question.code, text);
  };

  const handleType = (e) => {
    const text = e.target.value;
    setDraft(text);
    dirty.current = true;
    // A suggestion made about words that have since changed is about nothing.
    if (assist) setAssist(null);
    clearTimeout(timer.current);
    // Long enough that a sentence saves as one write, short enough that
    // walking away from the tablet does not lose it.
    timer.current = setTimeout(() => flush(text), 900);
  };

  /*
   * Tier 3 — tidy the note into the sentence the report needs, now.
   *
   * §8 calls this the highest-leverage of the four tiers, and the reason is
   * the timing rather than the prose: report quality is decided in this box
   * while the auditor is standing in front of the thing, and fixing it two
   * weeks later is strictly worse.
   *
   * It never writes. The suggestion is offered beside the auditor's own words
   * and only becomes the comment if they take it — which is also the moment
   * the provenance flag is set, so a reviewer can tell the two apart.
   */
  const runAssist = async () => {
    const note = String(draft || "").trim();
    if (!note) return;

    // Save what they typed first. The call takes a second or two, and a note
    // lost to a closed tab while a suggestion was in flight would be theirs.
    flush(draft);
    setAssist({ state: "working" });

    try {
      const r = await api.assist(auditId, question.code, note);
      setAssist({ ...r, state: "done", asked: note });
    } catch (e) {
      setAssist({
        state: "done",
        usable: false,
        // The note is already saved, which is the thing worth saying — the
        // auditor should not feel they have to retype anything.
        advice: (e && e.message) || "Could not reach the assistant. Your note is saved.",
      });
    }
  };

  const takeSuggestion = (text) => {
    clearTimeout(timer.current);
    dirty.current = false;
    setDraft(text);
    setAssist(null);
    // The third argument is the provenance: these are the assist's words,
    // accepted. Any later keystroke saves without it and clears the flag.
    onComment(question.code, text, true);
  };

  // A carried row is a proposal until the auditor has looked at it (§8).
  // Unconfirmed ones are marked so they stand out in a wall of answered
  // questions — otherwise "everything is filled in" reads as "everything is
  // done", which is the mistake the gate exists to prevent.
  const carried = answer && answer.carried_from_audit_id ? answer : null;
  const confirmed = !!(answer && answer.is_confirmed);

  const cls = ["q",
    value ? "a-" + value : "",
    carried && !confirmed ? "carried-open" : "",
  ].filter(Boolean).join(" ");
  const evidence = String(question.evidence || "")
    .split(/[,;/]/)
    .map((s) => s.trim())
    .filter(Boolean);

  const needsNote = isGap(value) && !String(draft || "").trim();

  /*
   * When the assist is worth offering.
   *
   * Only on a gap, because a Yes has no finding to write up. Only with
   * something already typed — the whole tier is "tidy what you wrote", and a
   * button on an empty box invites somebody to expect it to write the note for
   * them, which is the one thing it must never do.
   *
   * `api.isOnline()` as well as the server's switch: a cached audit opened on
   * a plant floor still carries assist_enabled from when it was fetched, and
   * this needs a network.
   */
  const canAssist = shouldOfferAssist({
    enabled: assistEnabled,
    online: api.isOnline(),
    disabled,
    answer: value,
    note: draft,
  });

  return (
    <div className={cls} id={"q-" + question.code}>
      <div className="q-code">{question.code}</div>
      <div className="q-text">{question.question}</div>

      {evidence.map((e) => (
        <span className="q-evidence" key={e}>
          {e}
        </span>
      ))}

      <div className="q-answers">
        {OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            className={"ans" + (value === o.value ? " on-" + o.value : "")}
            disabled={disabled}
            aria-pressed={value === o.value}
            onClick={() => {
              // Tapping the current answer clears it, so a mis-tap is undoable
              // without a separate control.
              onAnswer(question.code, value === o.value ? null : o.value);
            }}
          >
            {o.label}
          </button>
        ))}
        {answer && answer.set_by_bulk && value === "yes" ? (
          <span className="bulk-flag">set in bulk</span>
        ) : null}
      </div>

      {scopedOutBy ? (
        <div className="q-hint">Scoped out — {scopedOutBy}</div>
      ) : null}

      {/* Sits under the answer buttons, above the note: you compare, then you
          answer, then you write. */}
      <PreviousLine
        previous={previous}
        carried={carried}
        confirmed={confirmed}
        disabled={disabled}
        onConfirm={() => onConfirm && onConfirm(question.code)}
      />

      {/*
        Labelled, not just placeheld. The prototype names the question the
        auditor is answering — "what did you see on site?" — and says plainly
        when it is required, rather than leaving them to discover it at submit.
      */}
      <div className={"q-cmt" + (needsNote ? " need" : "")}>
        <label htmlFor={"cmt-" + question.code}>
          What did you see on site?
          {isGap(value) ? <span className="req"> — required for No and Partial</span> : null}
        </label>
        <textarea
          id={"cmt-" + question.code}
          className="q-comment"
          rows={2}
          value={draft}
          disabled={disabled}
          placeholder={isGap(value) ? exampleFor(question.evidence) : "Optional"}
          onChange={handleType}
          onBlur={() => flush(draft)}
        />

        {canAssist || assist ? (
          <div className="q-assist">
            <button
              type="button"
              className="assist-go"
              disabled={!canAssist || (assist && assist.state === "working")}
              onClick={runAssist}
            >
              {assist && assist.state === "working" ? "Tidying up…" : "Tidy this up"}
            </button>
            {answer && answer.comment_assisted ? (
              // Said plainly rather than hidden. These words were approved by
              // this auditor, not written by them, and the report is signed.
              <span className="assist-mark">assisted</span>
            ) : null}
          </div>
        ) : null}

        {/*
          The suggestion sits beside the auditor's own words, never in place of
          them (design §11, control 6). An invented detail is only visible if
          there is something to compare it against, and the auditor is the only
          person who will ever know which of the two is true.
        */}
        {assist && assist.state === "done" && assist.usable ? (
          <div className={"assist-out" + (assist.clean ? "" : " check")}>
            <div className="assist-mine">
              <span className="assist-label">You wrote</span>
              {assist.asked}
            </div>
            <div className="assist-theirs">
              <span className="assist-label">Suggested</span>
              {assist.suggestion}
            </div>

            {(assist.flags || []).length ? (
              <ul className="assist-flags">
                {assist.flags.map((f, i) => (
                  <li key={i}>
                    <strong>{f.value}</strong> — {f.why}
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="assist-actions">
              <button
                type="button"
                className="assist-take"
                onClick={() => takeSuggestion(assist.suggestion)}
              >
                Use this
              </button>
              <button type="button" className="assist-drop" onClick={() => setAssist(null)}>
                Keep mine
              </button>
            </div>
          </div>
        ) : null}

        {/*
          A refusal, which is the more useful half of this tier. Tier 2 makes
          the same judgement two weeks later and can only stay silent; here the
          auditor is thirty seconds from being able to fix it.
        */}
        {assist && assist.state === "done" && !assist.usable ? (
          <div className="assist-advice">
            {assist.advice}
            <button type="button" className="assist-drop" onClick={() => setAssist(null)}>
              Dismiss
            </button>
          </div>
        ) : null}
      </div>

      <CsaQuestionPhotos
        auditId={auditId}
        questionCode={question.code}
        photos={photos}
        disabled={disabled}
        onChange={onPhotosChanged}
      />

      <button type="button" className="ref-toggle" onClick={() => setShowRef(!showRef)}>
        {showRef ? "▾ Hide reference" : "▸ Reference"}
      </button>

      {showRef ? (
        <dl className="ref">
          {question.yes_statement ? (
            <>
              <dt>If conformant</dt>
              <dd>{question.yes_statement}</dd>
            </>
          ) : null}
          {question.no_statement ? (
            <>
              <dt>If not conformant</dt>
              <dd>{question.no_statement}</dd>
            </>
          ) : null}
          {question.recommendation ? (
            <>
              <dt>Recommendation</dt>
              <dd>{question.recommendation}</dd>
            </>
          ) : null}
          {question.legal_reference ? (
            <>
              <dt>Reference</dt>
              <dd>{question.legal_reference}</dd>
            </>
          ) : null}
        </dl>
      ) : null}
    </div>
  );
}
